import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticsService } from 'app/core/services/analytics.service';
import { TranslateApiService } from 'app/core/services/translate-api.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { Item } from '../models/menu.model';

@Injectable()
export class MenuService {
  /** Observables del idioma selccionado */
  language$ = new BehaviorSubject<any>('ES');
  /** Observables que indica si se visualiza o no el home */
  home$ = new BehaviorSubject<any>(true);
  /** Indica si se visualiza o no el sidemenu */
  sideMenu$ = new BehaviorSubject<any>(false);
  section$ = new BehaviorSubject<any>('');
  /**
   * Indica si es mobile o no
   */
  isMobile$ = new BehaviorSubject<boolean>(true);
  /** Idioma seleccionado */
  lang = 'ES';
  constructor(
    private analyticsService: AnalyticsService,
    private router: Router,
    private route: ActivatedRoute,
    private translateApi: TranslateApiService
  ) {
    this.language$.subscribe(lang => {
      this.lang = lang;
    });
  }

  /**
   * Guarda el idioma seleccionado
   *
   * @param lang info del idioma
   */
  setLang(lang: string): void {
    this.language$.next(lang);
  }

  /**
   * Obtiene el observable del idioma actual
   *
   * @returns observable con el idioma
   */
  getLang(): Observable<any> {
    return this.language$.asObservable();
  }

  /**
   * Guarda el estado del home
   *
   * @param value valor
   */
  setHome(value: boolean): void {
    this.home$.next(value);
  }

  /**
   * Obtiene el observable que indica
   * si se visualiza el home
   *
   * @returns observable con el valor
   */
  getHome(): Observable<any> {
    return this.home$.asObservable();
  }

  /**
   * Guarda el estado del sidemenu
   *
   * @param value valor
   */
  setSideMenu(value: boolean): void {
    this.sideMenu$.next(value);
  }

  /**
   * Obtiene el observable que indica
   * si se visualiza el sidemenu
   *
   * @returns observable con el valor
   */
  getSideMenu(): Observable<any> {
    return this.sideMenu$.asObservable();
  }


  /**
   * Guarda la informacion si es mobile o no
   *
   * @param value
   */
  setIsMobile(value: boolean): void {
    this.isMobile$.next(value);
  }

  /**
   * Obtiene el observable del isMobile
   *
   * @returns observable con la info del restaurante
   */
  getIsMobile(): Observable<boolean> {
    return this.isMobile$.asObservable();
  }

  /**
   * Envia la ifno al analytics
   *
   * @param item item seleccionado
   * @param departament tipo de menu donde esta el item
   * @param type nombre del evento
   */
  sendAnalyticsInfo(item: Item, departament: string, type: string) {

    const info = {
      restaurant_id: this.router.url.split('/')[2],
      menu_type: departament,
      type,
      product_id: item.name
    };
    this.analyticsService.sendAnalyticsInfo(type, info);
  }

  /**
   *  Valida si el color que se envia es de la gama de blancos
   *
   * @param color el color a analizar
   * @returns boolean
   */
  isWhite(color: string): boolean {
    const match = color.substring(1).match(/.{2}/g);

    if (!match) {
      return false;
    }

    const [r, g, b] = match.map(hex => parseInt(hex, 16));

    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;

    return luminance >= 200;
  }

  getLabelMenu(label: string): string {
    if (this.lang === 'ES') {
      return label;
    } else {
      return this.translateToEn(label).toLocaleUpperCase();
    }
  }

  /**
   * Traduce un label libre a inglés de forma remota y cacheada.
   * Devuelve un Observable<string> con la traducción o el label original en caso de error.
   */
  getLabelMenuAsync(label: string): Observable<string> {
    if (this.lang === 'ES') {
      return of(label);
    }

    // Si el label coincide con una clave conocida, devolver la traducción local rápida
    const translatedLocal = this.translateToEn(label);
    if (translatedLocal !== label) {
      return of(translatedLocal);
    }

    // Texto libre: traducir vía API
    return this.translateApi.translate(label, 'en');
  }

  private translateToEn(label: string): string {
    if (!label) {
      return label;
    }

    const key = label.normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '').toLowerCase();

    if (key === 'merch&hookah') {
      return 'MERCH & HOOKAH';
    }
    const dict: { [k: string]: string } = {
      inicio: 'Home',
      menu: 'Menu',
      comida: 'Food',
      bebidas: 'Drinks',
      postres: 'Desserts',
      cocteles: 'Cocktails',
      brunch: 'Brunch',
      extras: 'Extras',
      reserva: 'Booking',
      merch: 'Merch & Hookah',
      cervezas: 'Beers',
      licores: 'Liquors',
    };

    return dict[key] || label;
  }
}
